"use client";

import {
  Check,
  Edit3,
  ImageOff,
  Layers,
  Loader2,
  Megaphone,
  Palette,
  Plus,
  Search,
  Trash2,
  Upload,
  X
} from "lucide-react";
import Image from "next/image";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AdminPagination } from "@/components/admin/admin-pagination";
import { formatPrice } from "@/lib/utils";
import type { Product, ProductVariant } from "@/types";

function slugify(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

const EMPTY_FORM = {
  title: "",
  slug: "",
  brand: "",
  category: "",
  description: "",
  image: "",
  price: "",
  mrp: "",
  discount: "10",
  stock: "20",
  tags: "",
  isFeatured: false,
  isPrime: true,
  isSponsored: false,
  isFlashDeal: false,
  inPromotionBanner: false,
};

type FormState = typeof EMPTY_FORM;

type VariantForm = {
  id: string;
  color: string;
  colorCode: string;
  image: string;
  price: string;
  mrp: string;
  stock: string;
};

const COLOR_PRESETS = [
  { name: "Space Black", hex: "#18181b" },
  { name: "Glacier Silver", hex: "#e2e8f0" },
  { name: "Midnight Blue", hex: "#1e3a8a" },
  { name: "Forest Green", hex: "#14532d" },
  { name: "Rose Gold", hex: "#b76e79" },
  { name: "Sunset Red", hex: "#dc2626" },
  { name: "Titanium Gray", hex: "#475569" },
  { name: "Pure White", hex: "#ffffff" },
];

let categoriesCache: string[] | null = null;

export function ProductManager({ initialProducts }: { initialProducts: Product[] }) {
  const [products, setProducts] = useState(initialProducts);
  useEffect(() => setProducts(initialProducts), [initialProducts]);
  const [editing, setEditing] = useState<Product | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [additionalImages, setAdditionalImages] = useState<string[]>([]);
  const [newSubUrl, setNewSubUrl] = useState("");
  const [variants, setVariants] = useState<VariantForm[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("All");
  const [filterPlacement, setFilterPlacement] = useState("All");
  const [updatingBannerId, setUpdatingBannerId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  // Quick 1-click toggle for promotion banner placement from the table
  async function togglePromotionBanner(product: Product) {
    const nextState = !product.inPromotionBanner;
    setUpdatingBannerId(product.id);
    try {
      const res = await fetch(`/api/admin/products/${product.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ inPromotionBanner: nextState })
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(data?.message || "Failed to update promotion banner setting");
      }
      setProducts((items) =>
        items.map((item) =>
          item.id === product.id ? { ...item, inPromotionBanner: nextState } : item
        )
      );
      toast.success(
        nextState
          ? `"${product.title}" will now display in the promotion banner!`
          : `"${product.title}" removed from promotion banner.`
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update banner setting");
    } finally {
      setUpdatingBannerId(null);
    }
  }

  // Load categories from DB (with cache to prevent duplicate fetches across page navigation)
  const [dbCategories, setDbCategories] = useState<string[]>(() => categoriesCache || []);
  useEffect(() => {
    if (categoriesCache && categoriesCache.length > 0) {
      setForm((f) => (f.category ? f : { ...f, category: categoriesCache![0] }));
      return;
    }
    fetch("/api/admin/categories")
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data.categories) && data.categories.length > 0) {
          const names: string[] = data.categories.map((c: { name: string }) => c.name);
          categoriesCache = names;
          setDbCategories(names);
          setForm((f) => (f.category ? f : { ...f, category: names[0] }));
        }
      })
      .catch(() => {});
  }, []);

  function startEdit(product: Product) {
    setEditing(product);
    setErrors({});
    setImageError(false);
    setForm({
      title: product.title,
      slug: product.slug,
      brand: product.brand,
      category: product.category,
      description: product.description,
      image: product.images[0] ?? "",
      price: String(product.price),
      mrp: String(product.mrp),
      discount: String(product.discount),
      stock: String(product.stock),
      tags: product.tags.join(", "),
      isFeatured: product.isFeatured ?? false,
      isPrime: product.isPrime ?? true,
      isSponsored: product.isSponsored ?? false,
      isFlashDeal: product.isFlashDeal ?? false,
      inPromotionBanner: product.inPromotionBanner ?? false,
    });
    setAdditionalImages(product.images.slice(1));
    setVariants(
      product.variants && product.variants.length > 0
        ? product.variants.map((v) => ({
            id: v.id || Math.random().toString(36).substring(2, 9),
            color: v.color,
            colorCode: v.colorCode || "#000000",
            image: v.image,
            price: String(v.price),
            mrp: v.mrp ? String(v.mrp) : "",
            stock: v.stock !== undefined ? String(v.stock) : String(product.stock),
          }))
        : []
    );
    // Scroll smoothly to form
    formRef.current?.scrollIntoView({ behavior: "smooth" });
  }

  function cancelEdit() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setAdditionalImages([]);
    setNewSubUrl("");
    setVariants([]);
    setErrors({});
    setImageError(false);
  }

  function set(key: keyof FormState, value: string | boolean) {
    setForm((f) => ({ ...f, [key]: value }));
    if (key === "title" && !editing && typeof value === "string") {
      setForm((f) => ({ ...f, title: value, slug: slugify(value) }));
    }
    if (errors[key]) {
      setErrors((e) => {
        const next = { ...e };
        delete next[key];
        return next;
      });
    }
  }

  function handleAddSubUrl() {
    const url = newSubUrl.trim();
    if (!url) return;
    if (additionalImages.includes(url) || url === form.image.trim()) {
      toast.error("This image URL is already in the gallery.");
      return;
    }
    setAdditionalImages((prev) => [...prev, url]);
    setNewSubUrl("");
  }

  function handleRemoveSubUrl(index: number) {
    setAdditionalImages((prev) => prev.filter((_, i) => i !== index));
  }

  function addVariant(preset?: { name: string; hex: string }) {
    const newId = Math.random().toString(36).substring(2, 9);
    setVariants((prev) => [
      ...prev,
      {
        id: newId,
        color: preset?.name ?? "",
        colorCode: preset?.hex ?? "#18181b",
        image: "",
        price: form.price || "",
        mrp: form.mrp || "",
        stock: form.stock || "15",
      },
    ]);
  }

  function updateVariant(id: string, field: keyof VariantForm, value: string) {
    setVariants((prev) =>
      prev.map((v) => (v.id === id ? { ...v, [field]: value } : v))
    );
  }

  function removeVariant(id: string) {
    setVariants((prev) => prev.filter((v) => v.id !== id));
  }

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!form.title.trim() || form.title.trim().length < 3) e.title = "At least 3 characters";
    if (!form.brand.trim() || form.brand.trim().length < 2) e.brand = "At least 2 characters";
    if (!form.description.trim() || form.description.trim().length < 12)
      e.description = "At least 12 characters";
    if (!form.image.trim()) e.image = "Main image URL is required";
    if (!form.price || Number(form.price) <= 0) e.price = "Must be a positive number";
    if (!form.mrp || Number(form.mrp) <= 0) e.mrp = "Must be a positive number";
    if (Number(form.discount) < 0 || Number(form.discount) > 95) e.discount = "Between 0 and 95";
    if (!form.stock || Number(form.stock) < 0) e.stock = "Cannot be negative";

    // Validate variants
    for (let i = 0; i < variants.length; i++) {
      const v = variants[i];
      if (!v.color.trim()) {
        toast.error(`Variant #${i + 1}: Color name is required.`);
        return false;
      }
      if (!v.image.trim()) {
        toast.error(`Variant "${v.color}": Sub URL (variant image) is required.`);
        return false;
      }
      if (!v.price || Number(v.price) <= 0) {
        toast.error(`Variant "${v.color}": Price must be greater than 0.`);
        return false;
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!validate()) {
      toast.error("Fix the highlighted fields before saving.");
      return;
    }
    setLoading(true);

    const slug = form.slug.trim() || slugify(form.title);

    // Combine main image + additional gallery sub-urls
    const allImages = [
      form.image.trim(),
      ...additionalImages.map((img) => img.trim()).filter(Boolean),
    ];

    // Compile color variants with custom pricing and individual sub-URLs
    const compiledVariants: ProductVariant[] = variants.map((v) => ({
      id: v.id,
      color: v.color.trim(),
      colorCode: v.colorCode || "#000000",
      image: v.image.trim(),
      price: Number(v.price) || Number(form.price),
      mrp: v.mrp ? Number(v.mrp) : (form.mrp ? Number(form.mrp) : undefined),
      stock: Number(v.stock) || Number(form.stock),
    }));

    const payload = {
      title: form.title.trim(),
      slug,
      brand: form.brand.trim(),
      category: form.category,
      description: form.description.trim(),
      images: allImages,
      variants: compiledVariants,
      price: Number(form.price),
      mrp: Number(form.mrp),
      discount: Number(form.discount),
      stock: Number(form.stock),
      rating: editing?.rating ?? 0,
      reviewCount: editing?.reviewCount ?? 0,
      tags: form.tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      isPrime: form.isPrime,
      isFeatured: form.isFeatured,
      isSponsored: form.isSponsored,
      isFlashDeal: form.isFlashDeal,
      inPromotionBanner: form.inPromotionBanner,
    };

    try {
      const url = editing ? `/api/admin/products/${editing.id}` : "/api/admin/products";
      const method = editing ? "PATCH" : "POST";
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.message ?? `Server error ${response.status}`);
      }

      const saved: Product = data.product;
      setProducts((items) =>
        editing
          ? items.map((item) => (item.id === editing.id ? saved : item))
          : [saved, ...items]
      );
      cancelEdit();
      toast.success(editing ? "Product updated" : "Product added", {
        description: `Saved with ${compiledVariants.length} color variant(s) and ${allImages.length} image(s).`,
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to save product.");
    } finally {
      setLoading(false);
    }
  }

  async function deleteProduct(id: string) {
    const previous = products;
    setProducts((items) => items.filter((item) => item.id !== id));
    setConfirmDeleteId(null);

    try {
      const response = await fetch(`/api/admin/products/${id}`, { method: "DELETE" });
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.message ?? "Unable to delete product.");
      }
      toast.success("Product removed");
    } catch (error) {
      setProducts(previous);
      toast.error(error instanceof Error ? error.message : "Unable to delete product.");
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => {
      const matchesSearch =
        !q ||
        p.title.toLowerCase().includes(q) ||
        p.brand.toLowerCase().includes(q) ||
        p.slug.toLowerCase().includes(q);
      const matchesCategory = filterCategory === "All" || p.category === filterCategory;

      let matchesPlacement = true;
      if (filterPlacement === "Banner") {
        matchesPlacement = Boolean(p.inPromotionBanner);
      } else if (filterPlacement === "Featured") {
        matchesPlacement = Boolean(p.isFeatured);
      } else if (filterPlacement === "Deals") {
        matchesPlacement = Boolean(p.isFlashDeal);
      } else if (filterPlacement === "Sponsored") {
        matchesPlacement = Boolean(p.isSponsored);
      }

      return matchesSearch && matchesCategory && matchesPlacement;
    });
  }, [products, search, filterCategory, filterPlacement]);

  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const stats = useMemo(
    () => ({
      inventory: products.reduce((sum, p) => sum + p.stock, 0),
      averageDiscount: products.length
        ? Math.round(products.reduce((sum, p) => sum + p.discount, 0) / products.length)
        : 0,
      withVariants: products.filter((p) => p.variants && p.variants.length > 0).length,
      inBanner: products.filter((p) => p.inPromotionBanner).length,
    }),
    [products]
  );

  const inputClass = (field: string) =>
    `h-11 w-full rounded-md border bg-transparent px-3 outline-none transition focus:border-amazon-orange dark:bg-transparent ${
      errors[field]
        ? "border-red-500 focus:border-red-500"
        : "border-slate-200 dark:border-white/10"
    }`;

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {[
          ["Total products", products.length],
          ["In Promotion Banner", stats.inBanner],
          ["With Color Variants", stats.withVariants],
          ["Total inventory", stats.inventory],
          ["Avg discount", `${stats.averageDiscount}%`],
        ].map(([label, value]) => (
          <div
            key={String(label)}
            className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900"
          >
            <p className="text-xs font-black uppercase text-slate-500">{label}</p>
            <p className="mt-2 text-3xl font-black tracking-normal text-slate-950 dark:text-white">
              {value}
            </p>
          </div>
        ))}
      </div>

      {/* Add / Edit form */}
      <form
        ref={formRef}
        onSubmit={onSubmit}
        className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900"
      >
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-black text-slate-950 dark:text-white">
              {editing ? "Edit Product & Variants" : "Add New Product"}
            </h2>
            <p className="text-xs text-slate-500">
              Configure base product details, gallery images, and color variants with custom prices.
            </p>
          </div>
          {editing && (
            <button
              type="button"
              onClick={cancelEdit}
              className="text-sm font-bold text-amazon-teal hover:underline"
            >
              Cancel edit
            </button>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {/* Title */}
          <div className="md:col-span-2">
            <label className="mb-1 block text-sm font-bold">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder="e.g. Samsung Galaxy S24 Ultra 256GB"
              className={inputClass("title")}
            />
            {errors.title && <p className="mt-1 text-xs text-red-500">{errors.title}</p>}
          </div>

          {/* Slug */}
          <div>
            <label className="mb-1 block text-sm font-bold">Slug</label>
            <input
              value={form.slug}
              onChange={(e) => set("slug", e.target.value)}
              placeholder="auto-generated from title"
              className={inputClass("slug")}
            />
            <p className="mt-0.5 text-xs text-slate-400">Leave blank to auto-generate</p>
          </div>

          {/* Brand */}
          <div>
            <label className="mb-1 block text-sm font-bold">
              Brand <span className="text-red-500">*</span>
            </label>
            <input
              value={form.brand}
              onChange={(e) => set("brand", e.target.value)}
              placeholder="e.g. Samsung"
              className={inputClass("brand")}
            />
            {errors.brand && <p className="mt-1 text-xs text-red-500">{errors.brand}</p>}
          </div>

          {/* Category */}
          <div>
            <label className="mb-1 block text-sm font-bold">
              Category <span className="text-red-500">*</span>
            </label>
            <select
              value={form.category}
              onChange={(e) => set("category", e.target.value)}
              className="h-11 w-full rounded-md border border-slate-200 bg-transparent px-3 outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
            >
              {dbCategories.length === 0 && <option value="">Loading categories…</option>}
              {dbCategories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          {/* Base Price */}
          <div>
            <label className="mb-1 block text-sm font-bold">
              Base Price (ETB) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={form.price}
              onChange={(e) => set("price", e.target.value)}
              placeholder="e.g. 29999"
              className={inputClass("price")}
            />
            {errors.price && <p className="mt-1 text-xs text-red-500">{errors.price}</p>}
          </div>

          {/* MRP */}
          <div>
            <label className="mb-1 block text-sm font-bold">
              Base MRP (ETB) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={form.mrp}
              onChange={(e) => set("mrp", e.target.value)}
              placeholder="e.g. 34999"
              className={inputClass("mrp")}
            />
            {errors.mrp && <p className="mt-1 text-xs text-red-500">{errors.mrp}</p>}
          </div>

          {/* Discount */}
          <div>
            <label className="mb-1 block text-sm font-bold">Discount %</label>
            <input
              type="number"
              min="0"
              max="95"
              value={form.discount}
              onChange={(e) => set("discount", e.target.value)}
              className={inputClass("discount")}
            />
            {errors.discount && <p className="mt-1 text-xs text-red-500">{errors.discount}</p>}
          </div>

          {/* Base Stock */}
          <div>
            <label className="mb-1 block text-sm font-bold">
              Base Stock <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="0"
              value={form.stock}
              onChange={(e) => set("stock", e.target.value)}
              placeholder="e.g. 50"
              className={inputClass("stock")}
            />
            {errors.stock && <p className="mt-1 text-xs text-red-500">{errors.stock}</p>}
          </div>

          {/* Main Image URL */}
          <div className="md:col-span-2">
            <label className="mb-1 block text-sm font-bold">
              Main Product Image URL <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-3">
              <div className="flex-1">
                <input
                  type="url"
                  value={form.image}
                  onChange={(e) => {
                    set("image", e.target.value);
                    setImageError(false);
                  }}
                  placeholder="https://example.com/product-main.jpg"
                  className={inputClass("image")}
                />
                {errors.image && <p className="mt-1 text-xs text-red-500">{errors.image}</p>}
              </div>
              <div className="relative h-11 w-14 shrink-0 overflow-hidden rounded-md border border-slate-200 bg-slate-100 dark:border-white/10 dark:bg-slate-800">
                {form.image && !imageError ? (
                  <Image
                    src={form.image}
                    alt="Preview"
                    fill
                    className="object-contain p-1"
                    sizes="56px"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <ImageOff className="m-auto mt-3 h-5 w-5 text-slate-300" />
                )}
              </div>
            </div>
          </div>

          {/* Additional Gallery Sub-URLs */}
          <div className="md:col-span-2 rounded-lg border border-slate-200 bg-slate-50/50 p-4 dark:border-white/10 dark:bg-slate-900/40">
            <div className="flex items-center gap-2 mb-2">
              <Upload className="h-4 w-4 text-amazon-orange" />
              <label className="text-sm font-bold text-slate-900 dark:text-white">
                Additional Gallery Sub-URLs
              </label>
              <span className="text-xs text-slate-500">(Optional extra views & angles)</span>
            </div>

            <div className="flex gap-2">
              <input
                type="url"
                value={newSubUrl}
                onChange={(e) => setNewSubUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddSubUrl();
                  }
                }}
                placeholder="Paste sub-URL image link and click Add (e.g. https://...)"
                className="h-10 flex-1 rounded-md border border-slate-200 bg-white px-3 text-sm outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
              />
              <button
                type="button"
                onClick={handleAddSubUrl}
                className="inline-flex h-10 items-center gap-1 rounded-md bg-slate-900 px-3 text-xs font-bold text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900"
              >
                <Plus className="h-3.5 w-3.5" />
                Add Sub URL
              </button>
            </div>

            {additionalImages.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2.5">
                {additionalImages.map((url, idx) => (
                  <div
                    key={`${url}-${idx}`}
                    className="group relative flex items-center gap-2 rounded border border-slate-200 bg-white p-1.5 pr-2 dark:border-white/10 dark:bg-slate-900"
                  >
                    <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded bg-slate-100 dark:bg-slate-800">
                      <Image
                        src={url}
                        alt=""
                        fill
                        sizes="40px"
                        className="object-contain p-0.5"
                      />
                    </div>
                    <span className="max-w-[180px] truncate text-xs text-slate-600 dark:text-slate-300">
                      Sub URL #{idx + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveSubUrl(idx)}
                      className="text-slate-400 hover:text-red-500"
                      title="Remove sub URL"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Color Variants & Custom Pricing Section */}
          <div className="md:col-span-2 rounded-lg border-2 border-dashed border-amazon-orange/40 bg-amber-50/30 p-4 dark:bg-amber-950/10">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-200/60 pb-3 dark:border-white/10">
              <div>
                <div className="flex items-center gap-2">
                  <Palette className="h-5 w-5 text-amazon-orange" />
                  <h3 className="text-base font-bold text-slate-950 dark:text-white">
                    Color Variants & Sub-URLs with Custom Pricing
                  </h3>
                </div>
                <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-400">
                  Add color options with their specific sub-URL image and custom price (e.g. same product in different colors can have different prices and dedicated images).
                </p>
              </div>
              <button
                type="button"
                onClick={() => addVariant()}
                className="inline-flex h-9 items-center gap-1.5 rounded-md bg-amazon-orange px-3 text-xs font-bold text-slate-950 shadow-sm transition hover:bg-[#e07320]"
              >
                <Plus className="h-3.5 w-3.5" />
                Add Color Variant
              </button>
            </div>

            {/* Quick color preset chips */}
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              <span className="text-xs font-semibold text-slate-500">Quick Presets:</span>
              {COLOR_PRESETS.map((p) => (
                <button
                  key={p.name}
                  type="button"
                  onClick={() => addVariant(p)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700 shadow-xs hover:border-amazon-orange hover:text-amazon-orange dark:border-white/10 dark:bg-slate-900 dark:text-slate-300"
                >
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-full border border-black/20"
                    style={{ backgroundColor: p.hex }}
                  />
                  + {p.name}
                </button>
              ))}
            </div>

            {/* Variant Cards List */}
            {variants.length === 0 ? (
              <div className="mt-4 rounded-md border border-dashed border-slate-200 bg-white/60 p-6 text-center dark:border-white/10 dark:bg-slate-900/40">
                <Palette className="mx-auto h-8 w-8 text-slate-300 dark:text-slate-600" />
                <p className="mt-2 text-sm font-semibold text-slate-700 dark:text-slate-300">
                  No color variants added yet
                </p>
                <p className="text-xs text-slate-500">
                  If this product comes in different colors or has different prices per color, click above to add variants.
                </p>
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                {variants.map((v, index) => (
                  <div
                    key={v.id}
                    className="rounded-lg border border-slate-200 bg-white p-3.5 shadow-xs dark:border-white/10 dark:bg-slate-900"
                  >
                    <div className="flex items-center justify-between border-b border-slate-100 pb-2.5 dark:border-white/5">
                      <div className="flex items-center gap-2">
                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-[10px] font-black text-slate-600 dark:bg-white/10 dark:text-slate-300">
                          {index + 1}
                        </span>
                        <span
                          className="h-4 w-4 rounded-full border border-black/20"
                          style={{ backgroundColor: v.colorCode || "#000" }}
                        />
                        <span className="text-xs font-bold text-slate-900 dark:text-white">
                          {v.color || `Variant #${index + 1}`}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeVariant(v.id)}
                        className="inline-flex items-center gap-1 text-xs text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Remove
                      </button>
                    </div>

                    <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-12">
                      {/* Color Name */}
                      <div className="lg:col-span-3">
                        <label className="mb-1 block text-xs font-bold text-slate-700 dark:text-slate-300">
                          Color Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={v.color}
                          onChange={(e) => updateVariant(v.id, "color", e.target.value)}
                          placeholder="e.g. Space Black"
                          className="h-9 w-full rounded-md border border-slate-200 bg-transparent px-2.5 text-xs outline-none focus:border-amazon-orange dark:border-white/10"
                        />
                      </div>

                      {/* Color Picker Swatch */}
                      <div className="lg:col-span-2">
                        <label className="mb-1 block text-xs font-bold text-slate-700 dark:text-slate-300">
                          Color Swatch
                        </label>
                        <div className="flex h-9 items-center gap-1.5 rounded-md border border-slate-200 bg-transparent px-2 dark:border-white/10">
                          <input
                            type="color"
                            value={v.colorCode || "#000000"}
                            onChange={(e) => updateVariant(v.id, "colorCode", e.target.value)}
                            className="h-6 w-6 cursor-pointer rounded border-0 bg-transparent p-0"
                          />
                          <input
                            type="text"
                            value={v.colorCode}
                            onChange={(e) => updateVariant(v.id, "colorCode", e.target.value)}
                            placeholder="#000000"
                            className="w-16 bg-transparent text-xs uppercase outline-none"
                          />
                        </div>
                      </div>

                      {/* Sub URL / Variant Image */}
                      <div className="lg:col-span-4">
                        <label className="mb-1 block text-xs font-bold text-slate-700 dark:text-slate-300">
                          Sub-URL / Variant Image <span className="text-red-500">*</span>
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="url"
                            value={v.image}
                            onChange={(e) => updateVariant(v.id, "image", e.target.value)}
                            placeholder="https://.../black.jpg"
                            className="h-9 flex-1 rounded-md border border-slate-200 bg-transparent px-2.5 text-xs outline-none focus:border-amazon-orange dark:border-white/10"
                          />
                          <div className="relative h-9 w-10 shrink-0 overflow-hidden rounded border border-slate-200 bg-slate-50 dark:border-white/10 dark:bg-slate-800">
                            {v.image ? (
                              <Image
                                src={v.image}
                                alt=""
                                fill
                                sizes="40px"
                                className="object-contain p-0.5"
                              />
                            ) : (
                              <ImageOff className="m-auto mt-2 h-4 w-4 text-slate-300" />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Variant Price */}
                      <div className="lg:col-span-2">
                        <label className="mb-1 block text-xs font-bold text-slate-700 dark:text-slate-300">
                          Color Price (ETB) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={v.price}
                          onChange={(e) => updateVariant(v.id, "price", e.target.value)}
                          placeholder={form.price || "29999"}
                          className="h-9 w-full rounded-md border border-slate-200 bg-transparent px-2.5 text-xs font-bold outline-none focus:border-amazon-orange dark:border-white/10"
                        />
                      </div>

                      {/* Variant Stock */}
                      <div className="lg:col-span-1">
                        <label className="mb-1 block text-xs font-bold text-slate-700 dark:text-slate-300">
                          Stock
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={v.stock}
                          onChange={(e) => updateVariant(v.id, "stock", e.target.value)}
                          placeholder="15"
                          className="h-9 w-full rounded-md border border-slate-200 bg-transparent px-2 text-xs outline-none focus:border-amazon-orange dark:border-white/10"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tags */}
          <div className="md:col-span-2">
            <label className="mb-1 block text-sm font-bold">Tags</label>
            <input
              value={form.tags}
              onChange={(e) => set("tags", e.target.value)}
              placeholder="smartphone, android, 5g, flagship (comma separated)"
              className="h-11 w-full rounded-md border border-slate-200 bg-transparent px-3 outline-none focus:border-amazon-orange dark:border-white/10"
            />
          </div>

          {/* Description */}
          <div className="md:col-span-2">
            <label className="mb-1 block text-sm font-bold">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Detailed product description (min 12 characters)"
              rows={3}
              className={`w-full rounded-md border bg-transparent p-3 outline-none transition focus:border-amazon-orange dark:bg-transparent ${
                errors.description ? "border-red-500" : "border-slate-200 dark:border-white/10"
              }`}
            />
            {errors.description && (
              <p className="mt-1 text-xs text-red-500">{errors.description}</p>
            )}
          </div>
        </div>

        {/* Merchandising & Placement Controls */}
        <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/5">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Storefront Merchandising &amp; Placement
          </h3>
          <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-400">
            Control which curated storefront sections, deals, and badges this product appears in.
          </p>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
            <label className="flex items-start gap-2.5 rounded-md border border-amber-300 bg-amber-50/60 p-3 shadow-xs dark:border-amber-500/30 dark:bg-amber-950/20 cursor-pointer hover:border-amber-400 transition">
              <input
                type="checkbox"
                checked={form.inPromotionBanner}
                onChange={(e) => set("inPromotionBanner", e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-amber-600 rounded"
              />
              <div>
                <span className="block text-xs font-bold text-amber-950 dark:text-amber-200">🎉 Promotion Banner</span>
                <span className="text-[11px] text-amber-800/80 dark:text-amber-300/70">Show in hero banner</span>
              </div>
            </label>

            <label className="flex items-start gap-2.5 rounded-md border border-slate-200 bg-white p-3 shadow-xs dark:border-white/10 dark:bg-slate-900 cursor-pointer">
              <input
                type="checkbox"
                checked={form.isFeatured}
                onChange={(e) => set("isFeatured", e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-amazon-orange rounded"
              />
              <div>
                <span className="block text-xs font-bold text-slate-900 dark:text-white">Featured Product</span>
                <span className="text-[11px] text-slate-500">Homepage showcases</span>
              </div>
            </label>

            <label className="flex items-start gap-2.5 rounded-md border border-slate-200 bg-white p-3 shadow-xs dark:border-white/10 dark:bg-slate-900 cursor-pointer">
              <input
                type="checkbox"
                checked={form.isFlashDeal}
                onChange={(e) => set("isFlashDeal", e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-amazon-orange rounded"
              />
              <div>
                <span className="block text-xs font-bold text-slate-900 dark:text-white">⚡ Today&apos;s Deal</span>
                <span className="text-[11px] text-slate-500">Flash &amp; Lightning deals</span>
              </div>
            </label>

            <label className="flex items-start gap-2.5 rounded-md border border-slate-200 bg-white p-3 shadow-xs dark:border-white/10 dark:bg-slate-900 cursor-pointer">
              <input
                type="checkbox"
                checked={form.isPrime}
                onChange={(e) => set("isPrime", e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-amazon-orange rounded"
              />
              <div>
                <span className="block text-xs font-bold text-slate-900 dark:text-white">⭐ Prime Exclusive</span>
                <span className="text-[11px] text-slate-500">Prime badge &amp; deals</span>
              </div>
            </label>

            <label className="flex items-start gap-2.5 rounded-md border border-slate-200 bg-white p-3 shadow-xs dark:border-white/10 dark:bg-slate-900 cursor-pointer">
              <input
                type="checkbox"
                checked={form.isSponsored}
                onChange={(e) => set("isSponsored", e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-amazon-orange rounded"
              />
              <div>
                <span className="block text-xs font-bold text-slate-900 dark:text-white">📢 Sponsored</span>
                <span className="text-[11px] text-slate-500">Paid promotion slot</span>
              </div>
            </label>
          </div>
        </div>

        <div className="mt-5 flex items-center gap-3">
          <Button type="submit" disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {editing ? "Update Product & Variants" : "Add Product & Variants"}
          </Button>
          {editing && (
            <Button type="button" variant="outline" onClick={cancelEdit}>
              Cancel
            </Button>
          )}
        </div>
      </form>

      {/* Search + filter */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-48 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search by title, brand, slug…"
            className="h-10 w-full rounded-md border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
          />
          {search && (
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setPage(1);
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <select
          value={filterCategory}
          onChange={(e) => {
            setFilterCategory(e.target.value);
            setPage(1);
          }}
          className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
        >
          <option value="All">All categories</option>
          {dbCategories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          value={filterPlacement}
          onChange={(e) => {
            setFilterPlacement(e.target.value);
            setPage(1);
          }}
          className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
        >
          <option value="All">All placements</option>
          <option value="Banner">🎉 In Promotion Banner ({stats.inBanner})</option>
          <option value="Featured">⭐ Featured</option>
          <option value="Deals">⚡ Deals</option>
          <option value="Sponsored">📢 Sponsored</option>
        </select>
        <p className="text-sm text-slate-500">
          {filtered.length} result{filtered.length !== 1 ? "s" : ""}
        </p>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="bg-slate-100 text-xs uppercase text-slate-500 dark:bg-white/10">
              <tr>
                <th className="px-4 py-3">Product</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Color Variants</th>
                <th className="px-4 py-3">Price</th>
                <th className="px-4 py-3">Stock</th>
                <th className="px-4 py-3">Promotion Banner</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-white/10">
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-slate-500">
                    {products.length === 0
                      ? "No products in the database yet. Add your first product above."
                      : "No products match your search."}
                  </td>
                </tr>
              ) : (
                paginated.map((product) => {
                  const productVariants = product.variants || [];
                  const variantPrices = productVariants.map((v) => v.price);
                  const minPrice = variantPrices.length > 0 ? Math.min(...variantPrices) : product.price;
                  const maxPrice = variantPrices.length > 0 ? Math.max(...variantPrices) : product.price;

                  return (
                    <tr key={product.id} className="hover:bg-slate-50 dark:hover:bg-white/5">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded bg-slate-100 dark:bg-slate-800">
                            <Image
                              src={product.images[0]}
                              alt=""
                              fill
                              sizes="40px"
                              className="object-contain p-0.5"
                            />
                          </div>
                          <div className="min-w-0">
                            <p className="line-clamp-1 font-bold text-slate-950 dark:text-white">
                              {product.title}
                            </p>
                            <p className="text-xs text-slate-500">{product.brand}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">{product.category}</td>
                      <td className="px-4 py-3">
                        {productVariants.length > 0 ? (
                          <div className="flex items-center gap-1.5">
                            <div className="flex -space-x-1">
                              {productVariants.slice(0, 4).map((v) => (
                                <span
                                  key={v.id}
                                  title={`${v.color} - ${formatPrice(v.price)}`}
                                  className="inline-block h-4 w-4 rounded-full border border-white shadow-xs dark:border-slate-800"
                                  style={{ backgroundColor: v.colorCode || "#000" }}
                                />
                              ))}
                            </div>
                            <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                              {productVariants.length} color{productVariants.length > 1 ? "s" : ""}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">Standard</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-bold">
                        {minPrice !== maxPrice ? (
                          <div>
                            <span className="text-xs text-slate-500 font-normal">From </span>
                            <span>{formatPrice(minPrice)}</span>
                          </div>
                        ) : (
                          formatPrice(product.price)
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={product.stock === 0 ? "font-bold text-red-500" : ""}>
                          {product.stock}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => togglePromotionBanner(product)}
                          disabled={updatingBannerId === product.id}
                          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold transition ${
                            product.inPromotionBanner
                              ? "bg-amber-100 text-amber-900 ring-1 ring-amber-400/40 hover:bg-amber-200 dark:bg-amber-950/60 dark:text-amber-200 dark:ring-amber-500/30"
                              : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/10 dark:text-slate-400 dark:hover:bg-white/15"
                          }`}
                          title={
                            product.inPromotionBanner
                              ? "Click to remove from homepage promotion banner"
                              : "Click to feature in homepage promotion banner"
                          }
                        >
                          {updatingBannerId === product.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Megaphone
                              className={`h-3 w-3 ${
                                product.inPromotionBanner ? "text-amber-600 dark:text-amber-400 fill-amber-500/20" : "text-slate-400"
                              }`}
                            />
                          )}
                          <span>{product.inPromotionBanner ? "In Banner" : "Off"}</span>
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {product.inPromotionBanner && (
                            <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                              🎉 In Banner
                            </Badge>
                          )}
                          {product.isFeatured && (
                            <Badge tone="success">Featured</Badge>
                          )}
                          {product.isFlashDeal && (
                            <Badge tone="deal">⚡ Deal</Badge>
                          )}
                          {product.isPrime && (
                            <Badge tone="prime">Prime</Badge>
                          )}
                          {product.isSponsored && (
                            <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300">
                              Sponsored
                            </Badge>
                          )}
                          {!product.inPromotionBanner && !product.isFeatured && !product.isFlashDeal && !product.isSponsored && (
                            <Badge>Standard</Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => startEdit(product)}
                            className="rounded-md p-2 hover:bg-slate-100 dark:hover:bg-white/10"
                            title="Edit Product & Variants"
                          >
                            <Edit3 className="h-4 w-4" />
                          </button>
                          {confirmDeleteId === product.id ? (
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => deleteProduct(product.id)}
                                className="rounded-md bg-red-600 px-2 py-1 text-xs font-bold text-white hover:bg-red-700"
                              >
                                Confirm
                              </button>
                              <button
                                type="button"
                                onClick={() => setConfirmDeleteId(null)}
                                className="rounded-md px-2 py-1 text-xs font-bold text-slate-600 hover:bg-slate-100 dark:text-slate-300"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setConfirmDeleteId(product.id)}
                              className="rounded-md p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <AdminPagination
        currentPage={page}
        totalItems={filtered.length}
        perPage={perPage}
        onPageChange={setPage}
        onPerPageChange={(newPerPage) => {
          setPerPage(newPerPage);
          setPage(1);
        }}
        itemLabel="products"
      />
    </div>
  );
}
